import { useContext, useRef, useState } from "react";
import {
  FirmaFiltersContext,
  FirmaFiltersEventContext,
} from "../../../../../context/FiltersContext";
import {
  FirmaCVRFilterSetting,
  FirmaFilterKeys,
  QueryFilter,
} from "../../../../../interfaces/filters";
import { AccordionFilterItem } from "../../../../accordion/Accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../../select/Select";
import AppliedFilterItem from "../../../../filter/AppliedFilterItem";
import { ComboBox } from "../../../../filter/ComboBox";
import {
  dateQueries,
  numberQueries,
} from "../../../../../config/filter/CustomFilterConfig";
import { NumberQuery } from "../../../../filter/custom/types";
import { DatePicker, useToaster, Notification } from "rsuite";
import { isCompleteDate } from "../../../../formcontrols/datePickerValue";
import {
  cvrNrStatusMapper,
  pNrStatusMapper,
} from "../../../../../util/cvrStatusMapper";
import SuperDropdown from "../../../../dropdown/SuperDropdown";
import DefaultDropdownItem from "../../../../dropdown/DefaultDropdownItem";
import { format } from "date-fns";
import WarningIcon from "../../../../icons/WarningIcon";
import { f } from "../../../../../util/f";
import { FirmaDtoFilterInput } from "../../../../../interfaces/schema";

// helpers
const filterKey: QueryFilter<FirmaFilterKeys>["filterKey"] = "cvr";

type UpdateEvent =
  | {
      type: "cvrNr";
    }
  | {
      type: "cvrStatus";
      statusItem: (typeof cvrNrStatusMapper)[number];
    }
  | {
      type: "pNrStatus";
      statusItem: (typeof pNrStatusMapper)[number];
    }
  | {
      type: "cvrEndDate";
    }
  | {
      type: "pNr";
    };

export default function FirmaCVRFilter() {
  // context
  const filters = useContext(FirmaFiltersContext);
  const updateFilters = useContext(FirmaFiltersEventContext);

  // find active settings
  const settings: FirmaCVRFilterSetting | undefined = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  // react things
  // state
  const [cvrNrSelectedQuery, setCvrNrSelectedQuery] = useState<NumberQuery>(
    numberQueries[0],
  );
  const [pNrSelectedQuery, setPNrSelectedQuery] = useState<NumberQuery>(
    numberQueries[0],
  );
  const [selectedEndDateQuery, setSelectedEndDateQuery] = useState(
    dateQueries[0],
  );

  const [isEndDateBetween, setIsEndDateBetween] = useState<boolean>(false);

  const [selectedEndDateFrom, setSelectedEndDateFrom] = useState<Date | null>(
    new Date(),
  );
  const [selectedEndDateTo, setSelectedEndDateTo] = useState<Date | null>(
    new Date(),
  );

  // refs
  const cvrNrInputRef = useRef<HTMLInputElement>(null);
  const pNrInputRef = useRef<HTMLInputElement>(null);

  // toasting
  const toaster = useToaster();

  // helpers
  function buildFilter(settings: FirmaCVRFilterSetting) {
    return f<FirmaDtoFilterInput>()
      .addFilter("and", [
        {
          and: [
            settings.cvrEndDateSettings.length
              ? {
                  or: settings.cvrEndDateSettings.map((set) => set.filter),
                }
              : {},
            settings.cvrNrSettings.length
              ? {
                  or: settings.cvrNrSettings.map((set) => set.filter),
                }
              : {},
            settings.cvrStatusSettings.length
              ? {
                  or: settings.cvrStatusSettings.map((set) => {
                    return {
                      cvrNrStatusCvr: {
                        eq: set.value !== "null" ? set.value : null,
                      },
                    };
                  }),
                }
              : {},
            settings.pNrSettings.length
              ? {
                  or: settings.pNrSettings.map((set) => set.filter),
                }
              : {},
            settings.pNrStatusSettings.length
              ? {
                  or: settings.pNrStatusSettings.map((set) => {
                    return {
                      pnrStatusCvr: {
                        eq: set.value !== "null" ? set.value : null,
                      },
                    };
                  }),
                }
              : {},
          ],
        },
      ])
      .build();
  }

  function toastFilterExists() {
    toaster.push(
      <Notification>
        <div className="d-flex flex-row gap-3 align-items-center">
          <span className={`c-theme-icon text-light bg-warning`}>
            <WarningIcon />
          </span>
          Filteret er allerede tilføjet.
        </div>
      </Notification>,
      {
        placement: "bottomEnd",
        duration: 2500,
      },
    );
  }

  // handlers
  function updateEventHandler(event: UpdateEvent) {
    const cvrEndDateSettings: FirmaCVRFilterSetting["cvrEndDateSettings"] =
      settings?.cvrEndDateSettings || [];
    if (event.type === "cvrEndDate") {
      // OK is disabled while the date is unfinished; the to-date is only
      // read when "between" is chosen (ADV-320).
      if (!selectedEndDateFrom) return;
      const selectedEndDateFromFormattedPretty = format(
        selectedEndDateFrom,
        "dd-MM-yyyy",
      );
      const selectedEndDateFromFormattedFilter = format(
        selectedEndDateFrom,
        "yyyy-MM-dd",
      );
      const selectedEndDateToFormattedPretty = format(
        selectedEndDateTo ?? selectedEndDateFrom,
        "dd-MM-yyyy",
      );
      const selectedEndDateToFormattedFilter = format(
        selectedEndDateTo ?? selectedEndDateFrom,
        "yyyy-MM-dd",
      );

      let endDateItem: FirmaCVRFilterSetting["cvrEndDateSettings"][number];

      if (isEndDateBetween) {
        endDateItem = {
          key: `gt-${selectedEndDateFromFormattedPretty}-lt-${selectedEndDateToFormattedPretty}`,
          displayName: `${selectedEndDateFromFormattedPretty} - ${selectedEndDateToFormattedPretty}`,
          selectedQueryName: "Mellem",
          filter: {
            and: [
              {
                pnrOphoertCvr: {
                  gt: selectedEndDateFromFormattedFilter,
                },
              },
              {
                pnrOphoertCvr: {
                  lt: selectedEndDateToFormattedFilter,
                },
              },
            ],
          },
        };
      } else {
        endDateItem = {
          key: `${selectedEndDateQuery.query}-${selectedEndDateFromFormattedPretty}`,
          displayName: selectedEndDateFromFormattedPretty,
          selectedQueryName: selectedEndDateQuery.displayName,
          filter: {
            pnrOphoertCvr: {
              [selectedEndDateQuery.query]: selectedEndDateFromFormattedFilter,
            },
          },
        };
      }

      const filterExists = cvrEndDateSettings.some(
        (item) => item.key === endDateItem.key,
      );

      if (filterExists) {
        toastFilterExists();
      } else {
        cvrEndDateSettings.push(endDateItem);
      }
    }

    const cvrNrSettings: FirmaCVRFilterSetting["cvrNrSettings"] =
      settings?.cvrNrSettings || [];
    if (event.type === "cvrNr") {
      const cvrInputValue = cvrNrInputRef.current?.value;
      if (cvrInputValue) {
        const settingsItem: FirmaCVRFilterSetting["cvrNrSettings"][number] = {
          cvrNr: Number(cvrInputValue),
          selectedQueryName: cvrNrSelectedQuery.displayName,
          key: `${cvrNrSelectedQuery.query}-${cvrInputValue}`,
          filter: {
            cvrNr: {
              [cvrNrSelectedQuery.query]: Number(cvrInputValue),
            },
          },
        };

        const filterExists = cvrNrSettings.some(
          (set) => set.key === settingsItem.key,
        );

        if (filterExists) {
          toastFilterExists();
        } else {
          cvrNrSettings.push(settingsItem);
        }
      }
    }

    const cvrStatusSettings: FirmaCVRFilterSetting["cvrStatusSettings"] =
      settings?.cvrStatusSettings || [];
    if (event.type === "cvrStatus") {
      const settingsItem: FirmaCVRFilterSetting["cvrStatusSettings"][number] = {
        displayName: event.statusItem.displayName,
        value: event.statusItem.value,
      };

      const filterExist = cvrStatusSettings.some(
        (set) => set.value === settingsItem.value,
      );

      if (filterExist) {
        toastFilterExists();
      } else {
        cvrStatusSettings.push(settingsItem);
      }
    }

    const pNrStatusSettings: FirmaCVRFilterSetting["pNrStatusSettings"] =
      settings?.pNrStatusSettings || [];

    if (event.type === "pNrStatus") {
      const settingsItem: FirmaCVRFilterSetting["pNrStatusSettings"][number] = {
        displayName: event.statusItem.displayName,
        value: event.statusItem.value,
      };

      const filterExist = pNrStatusSettings.some(
        (set) => set.value === settingsItem.value,
      );

      if (filterExist) {
        toastFilterExists();
      } else {
        pNrStatusSettings.push(settingsItem);
      }
    }

    const pNrSettings: FirmaCVRFilterSetting["pNrSettings"] =
      settings?.pNrSettings || [];
    if (event.type === "pNr") {
      const pNrInputValue = pNrInputRef.current?.value;
      if (pNrInputValue) {
        const settingsItem: FirmaCVRFilterSetting["pNrSettings"][number] = {
          pNr: Number(pNrInputValue),
          selectedQueryName: pNrSelectedQuery.displayName,
          key: `${pNrSelectedQuery.query}-${pNrInputValue}`,
          filter: {
            pnr: {
              [pNrSelectedQuery.query]: Number(pNrInputValue),
            },
          },
        };

        const filterExists = pNrSettings.some(
          (set) => set.key === settingsItem.key,
        );

        if (filterExists) {
          toastFilterExists();
        } else {
          pNrSettings.push(settingsItem);
        }
      }
    }

    const updatedSettings: FirmaCVRFilterSetting = {
      cvrEndDateSettings: cvrEndDateSettings,
      cvrNrSettings: cvrNrSettings,
      cvrStatusSettings: cvrStatusSettings,
      pNrSettings: pNrSettings,
      pNrStatusSettings: pNrStatusSettings,
    };

    const canBuildFilter =
      updatedSettings.cvrEndDateSettings.length ||
      updatedSettings.cvrNrSettings.length ||
      updatedSettings.cvrStatusSettings.length ||
      updatedSettings.pNrSettings.length ||
      updatedSettings.pNrStatusSettings.length;
    const filterString = canBuildFilter ? buildFilter(updatedSettings) : "";

    const queryFilter: QueryFilter<FirmaFilterKeys, FirmaCVRFilterSetting> = {
      filter: filterString,
      filterKey: filterKey,
      settings: updatedSettings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(key: string) {
    const updatedSettings: FirmaCVRFilterSetting = {
      cvrEndDateSettings:
        settings?.cvrEndDateSettings.filter((set) => set.key !== key) || [],
      cvrNrSettings:
        settings?.cvrNrSettings.filter((set) => set.key !== key) || [],
      cvrStatusSettings:
        settings?.cvrStatusSettings.filter((set) => set.value !== key) || [],
      pNrSettings: settings?.pNrSettings.filter((set) => set.key !== key) || [],
      pNrStatusSettings:
        settings?.pNrStatusSettings.filter((set) => set.value !== key) || [],
    };

    const canBuildFilter =
      updatedSettings.cvrEndDateSettings.length ||
      updatedSettings.cvrNrSettings.length ||
      updatedSettings.cvrStatusSettings.length ||
      updatedSettings.pNrSettings.length ||
      updatedSettings.pNrStatusSettings.length;
    const filterString = canBuildFilter ? buildFilter(updatedSettings) : "";

    const queryFilter: QueryFilter<FirmaFilterKeys, FirmaCVRFilterSetting> = {
      filter: filterString,
      filterKey: filterKey,
      settings: updatedSettings,
      parentFilter: "and",
    };

    if (canBuildFilter) {
      updateFilters({
        event: "update",
        queryFilter: queryFilter,
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionFilterItem
      title="CVR-oplysninger"
      value="cvr"
      isActive={filters.some((f) => f.filterKey === filterKey)}
    >
      <h4 className="h6 mb-1 text-secondary">CVR-nummer</h4>
      <div className="c-applied-filter-group">
        {settings?.cvrNrSettings.map((set) => (
          <AppliedFilterItem
            labelPrefix={`${set.selectedQueryName}`}
            label={`${set.cvrNr}`}
            key={`${set.key}`}
            removeFilterHandler={() => {
              removeFilterHandler(set.key);
            }}
          />
        ))}
      </div>

      <ComboBox>
        <Select
          defaultValue={cvrNrSelectedQuery?.query}
          onValueChange={(value: string) => {
            const query = numberQueries.find((q) => q.query === value);
            if (query) {
              setCvrNrSelectedQuery(query);
            }
          }}
        >
          <SelectTrigger placeholder="Vælg query type" badge />
          <SelectContent small>
            {numberQueries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        <input
          className="form-control"
          placeholder={"CVR-nummer"}
          ref={cvrNrInputRef}
          type={"number"}
          required
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              updateEventHandler({
                type: "cvrNr",
              });
            }
          }}
        />

        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1 btn--combo-action`}
          onClick={() => {
            updateEventHandler({
              type: "cvrNr",
            });
          }}
        >
          OK
        </button>
      </ComboBox>

      <h4 className="h6 mb-1 mt-3 text-secondary">CVR-status for virksomhed</h4>

      <div className="c-applied-filter-group">
        {settings?.cvrStatusSettings.map((set) => (
          <AppliedFilterItem
            label={`${set.displayName}`}
            key={`${set.value}`}
            removeFilterHandler={() => {
              removeFilterHandler(set.value);
            }}
          />
        ))}
      </div>

      <SuperDropdown
        items={cvrNrStatusMapper}
        itemToString={(item) => item?.value ?? ""}
        itemsIdKey="value"
        placeholder="Søg CVR-status for virksomhed"
        filterKeys={["displayName"]}
        clearInputAfterSelection
        itemToElement={(item) => (
          <DefaultDropdownItem label={item.displayName} />
        )}
        onSelectedItemEvent={(item) => {
          if (item?.selectedItem) {
            updateEventHandler({
              type: "cvrStatus",
              statusItem: item.selectedItem,
            });
          }
        }}
      />

      <h4 className="h6 mb-1 mt-3 text-secondary">
        CVR-status for produktionsenhed
      </h4>

      <div className="c-applied-filter-group">
        {settings?.pNrStatusSettings.map((set) => (
          <AppliedFilterItem
            label={`${set.displayName}`}
            key={`${set.value}`}
            removeFilterHandler={() => {
              removeFilterHandler(set.value);
            }}
          />
        ))}
      </div>

      <SuperDropdown
        items={pNrStatusMapper}
        itemToString={(item) => item?.value ?? ""}
        itemsIdKey="value"
        placeholder="Søg CVR-status for produktionsenhed"
        filterKeys={["displayName"]}
        clearInputAfterSelection
        itemToElement={(item) => (
          <DefaultDropdownItem label={item.displayName} />
        )}
        onSelectedItemEvent={(item) => {
          if (item?.selectedItem) {
            updateEventHandler({
              type: "pNrStatus",
              statusItem: item.selectedItem,
            });
          }
        }}
      />

      <h4 className="h6 mb-1 mt-3 text-secondary">
        CVR ophørsdato for produktionsenhed
      </h4>

      <div className="c-applied-filter-group">
        {settings?.cvrEndDateSettings.map((set) => (
          <AppliedFilterItem
            labelPrefix={`${set.selectedQueryName}`}
            label={`${set.displayName}`}
            key={`${set.key}`}
            removeFilterHandler={() => {
              removeFilterHandler(set.key);
            }}
          />
        ))}
      </div>

      <ComboBox>
        <Select
          defaultValue={selectedEndDateQuery?.query}
          onValueChange={(value: string) => {
            if (value === "between") {
              setIsEndDateBetween(true);
              return;
            } else {
              setIsEndDateBetween(false);
            }

            const query = dateQueries.find((q) => q.query === value);
            if (query) {
              setSelectedEndDateQuery(query);
            }
          }}
        >
          <SelectTrigger aria-label="Vælg filterfunktion" badge />
          <SelectContent small>
            {dateQueries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
            <SelectItem value={"between"} key={"between"}>
              Mellem
            </SelectItem>
          </SelectContent>
        </Select>
        <DatePicker
          appearance="subtle"
          cleanable={false}
          defaultValue={selectedEndDateFrom}
          format="dd-MM-yyyy"
          oneTap
          placement="autoVertical"
          size="xs"
          onChange={(date) => {
            setSelectedEndDateFrom(isCompleteDate(date) ? date : null);
          }}
        />
        {isEndDateBetween && (
          <>
            -{" "}
            <DatePicker
              appearance="subtle"
              cleanable={false}
              defaultValue={selectedEndDateTo}
              format="dd-MM-yyyy"
              oneTap
              placement="autoVertical"
              size="xs"
              onChange={(date) => {
                setSelectedEndDateTo(isCompleteDate(date) ? date : null);
              }}
            />
          </>
        )}
        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1`}
          disabled={
            !selectedEndDateFrom ||
            (isEndDateBetween &&
              (!selectedEndDateTo || selectedEndDateFrom > selectedEndDateTo))
          }
          onClick={() => {
            updateEventHandler({
              type: "cvrEndDate",
            });
          }}
        >
          OK
        </button>
      </ComboBox>

      <h4 className="h6 mb-1 mt-3 text-secondary">P-nummer</h4>

      <div className="c-applied-filter-group">
        {settings?.pNrSettings.map((set) => (
          <AppliedFilterItem
            labelPrefix={`${set.selectedQueryName}`}
            label={`${set.pNr}`}
            key={`${set.key}`}
            removeFilterHandler={() => {
              removeFilterHandler(set.key);
            }}
          />
        ))}
      </div>

      <ComboBox>
        <Select
          defaultValue={pNrSelectedQuery?.query}
          onValueChange={(value: string) => {
            const query = numberQueries.find((q) => q.query === value);
            if (query) {
              setPNrSelectedQuery(query);
            }
          }}
        >
          <SelectTrigger placeholder="Vælg query type" badge />
          <SelectContent small>
            {numberQueries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        <input
          className="form-control"
          placeholder={"P-nummer"}
          ref={pNrInputRef}
          type={"number"}
          required
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              if (pNrInputRef.current?.value) {
                updateEventHandler({
                  type: "pNr",
                });
              }
            }
          }}
        />

        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1 btn--combo-action`}
          onClick={() => {
            updateEventHandler({
              type: "pNr",
            });
          }}
        >
          OK
        </button>
      </ComboBox>
    </AccordionFilterItem>
  );
}
